import { useState } from "react";
import { motion } from "framer-motion";
import { DangerZone } from "@/components/admin/DangerZone";
import {
  Building2, Users, CreditCard, Activity, TrendingUp, TrendingDown,
  ShieldCheck, Globe, Server, AlertTriangle, CheckCircle2, Clock,
  IndianRupee, BarChart3, Zap, FileText, Package, Receipt, BookOpen,
  Calculator, Truck, ArrowUpRight, ArrowDownRight, Percent, UserPlus, UserMinus
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, Legend, RadialBarChart, RadialBar
} from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

// ── Data ────────────────────────────────────────────────────────────────

const platformStats = [
  { label: "Total Companies", value: "0", change: "No data yet", icon: Building2, trend: "—", up: true },
  { label: "Active Users", value: "0", change: "No data yet", icon: Users, trend: "—", up: true },
  { label: "Monthly Revenue", value: "₹0", change: "No data yet", icon: IndianRupee, trend: "—", up: true },
  { label: "Platform Uptime", value: "99.9%", change: "Last 30 days", icon: Activity, trend: "Stable", up: true },
];

const revenueData = [
  { month: "Oct", revenue: 0, mrr: 0, arr: 0, users: 0, companies: 0 },
  { month: "Nov", revenue: 0, mrr: 0, arr: 0, users: 0, companies: 0 },
  { month: "Dec", revenue: 0, mrr: 0, arr: 0, users: 0, companies: 0 },
  { month: "Jan", revenue: 0, mrr: 0, arr: 0, users: 0, companies: 0 },
  { month: "Feb", revenue: 0, mrr: 0, arr: 0, users: 0, companies: 0 },
  { month: "Mar", revenue: 0, mrr: 0, arr: 0, users: 0, companies: 0 },
];

const growthData = [
  { month: "Oct", newCompanies: 0, churned: 0, netGrowth: 0, newUsers: 0, churnedUsers: 0 },
  { month: "Nov", newCompanies: 0, churned: 0, netGrowth: 0, newUsers: 0, churnedUsers: 0 },
  { month: "Dec", newCompanies: 0, churned: 0, netGrowth: 0, newUsers: 0, churnedUsers: 0 },
  { month: "Jan", newCompanies: 0, churned: 0, netGrowth: 0, newUsers: 0, churnedUsers: 0 },
  { month: "Feb", newCompanies: 0, churned: 0, netGrowth: 0, newUsers: 0, churnedUsers: 0 },
  { month: "Mar", newCompanies: 0, churned: 0, netGrowth: 0, newUsers: 0, churnedUsers: 0 },
];

const planDistribution = [
  { name: "Starter (₹499)", value: 0, color: "hsl(var(--muted-foreground))", revenue: 0, pct: 0 },
  { name: "Professional (₹999)", value: 0, color: "hsl(var(--accent))", revenue: 0, pct: 0 },
  { name: "Enterprise (₹1999)", value: 0, color: "hsl(var(--primary))", revenue: 0, pct: 0 },
  { name: "Trial (Free)", value: 0, color: "hsl(220 14% 80%)", revenue: 0, pct: 0 },
];

const subscriptionMetrics = {
  trialToPayConversion: 0,
  avgRevenuePerUser: 0,
  monthlyChurnRate: 0,
  lifetimeValue: 0,
  renewalRate: 0,
  trialExpiringSoon: 0,
  overdue: 0,
  upgradeRate: 0,
};

const featureUsage: { name: string; usage: number; sessions: number; icon: React.ComponentType<{ className?: string }> }[] = [];

const stateWiseData: { state: string; companies: number; users: number }[] = [];

const dailySignups = [
  { day: "Mon", signups: 0, churns: 0 },
  { day: "Tue", signups: 0, churns: 0 },
  { day: "Wed", signups: 0, churns: 0 },
  { day: "Thu", signups: 0, churns: 0 },
  { day: "Fri", signups: 0, churns: 0 },
  { day: "Sat", signups: 0, churns: 0 },
  { day: "Sun", signups: 0, churns: 0 },
];

const recentCompanies: { name: string; state: string; plan: string; status: string; users: number; revenue: string }[] = [];

const systemHealth = [
  { metric: "API Response Time", value: "—", status: "good" },
  { metric: "Database Load", value: "—", status: "good" },
  { metric: "Storage Used", value: "—", status: "good" },
  { metric: "Background Jobs", value: "—", status: "good" },
  { metric: "Error Rate (24h)", value: "—", status: "good" },
];

const marketplaceRevenueData = [
  { month: "Oct", revenue: 0, commission: 0 },
  { month: "Nov", revenue: 0, commission: 0 },
  { month: "Dec", revenue: 0, commission: 0 },
  { month: "Jan", revenue: 0, commission: 0 },
  { month: "Feb", revenue: 0, commission: 0 },
  { month: "Mar", revenue: 0, commission: 0 },
];

const popularServicesData: { name: string; value: number; color: string }[] = [];

const topPerformingCAs: { name: string; firm: string; city: string; rating: number; reviews: number; clients: number; revenue: string; verified: boolean }[] = [];

const requestStatusData = [
  { status: "Open", count: 0, color: "hsl(var(--accent))" },
  { status: "In Progress", count: 0, color: "hsl(var(--primary))" },
  { status: "Completed", count: 0, color: "hsl(var(--success, 142 71% 45%))" },
  { status: "Cancelled", count: 0, color: "hsl(var(--destructive))" },
];

// ── Helpers ─────────────────────────────────────────────────────────────

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-4 sm:p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const MetricCard = ({ label, value, sub, icon: Icon, positive }: { label: string; value: string; sub: string; icon?: React.ComponentType<{ className?: string }>; positive?: boolean }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
    {Icon && <div className="p-2 rounded-lg bg-accent/10"><Icon className="h-4 w-4 text-accent" /></div>}
    <div className="min-w-0">
      <p className="text-lg font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sub && <p className={`text-[10px] font-medium ${positive === undefined ? "text-muted-foreground" : positive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{sub}</p>}
    </div>
  </div>
);

// ── Component ───────────────────────────────────────────────────────────

const SuperAdminDashboard = () => {
  const [period, setPeriod] = useState("6m");

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-display text-foreground">Platform Analytics</h1>
          <p className="text-sm text-muted-foreground">Super Admin — AccouPro Platform Overview</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1m">Last 1 Month</SelectItem>
            <SelectItem value="3m">Last 3 Months</SelectItem>
            <SelectItem value="6m">Last 6 Months</SelectItem>
            <SelectItem value="1y">Last 1 Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {platformStats.map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Card>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs sm:text-sm text-muted-foreground">{stat.label}</span>
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                  <stat.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-accent" />
                </div>
              </div>
              <div className="text-lg sm:text-2xl font-bold font-display text-foreground">{stat.value}</div>
              <div className="flex items-center justify-between mt-1">
                <p className="text-[10px] sm:text-xs text-muted-foreground">{stat.change}</p>
                <span className={`text-[10px] sm:text-xs font-medium flex items-center gap-0.5 ${stat.up ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                  {stat.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />} {stat.trend}
                </span>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Tabbed Analytics Sections */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto scrollable-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="growth">Growth</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="marketplace">Marketplace</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
        </TabsList>

        {/* ── Overview Tab ── */}
        <TabsContent value="overview" className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">Revenue & User Growth</h3>
                <span className="text-xs text-muted-foreground">Last 6 months</span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="usrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis yAxisId="rev" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                  <YAxis yAxisId="usr" orientation="right" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip formatter={(value: number, name: string) => [name === "revenue" ? `₹${value.toLocaleString("en-IN")}` : value, name === "revenue" ? "Revenue" : "Users"]} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Area yAxisId="rev" type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" fill="url(#revGrad)" strokeWidth={2} name="Revenue" />
                  <Area yAxisId="usr" type="monotone" dataKey="users" stroke="hsl(var(--primary))" fill="url(#usrGrad)" strokeWidth={2} name="Users" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Plan Distribution</h3>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={planDistribution} cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={3} dataKey="value">
                    {planDistribution.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: number, name: string) => [v, name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {planDistribution.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                    <span className="text-muted-foreground truncate">{p.name}: <strong className="text-foreground">{p.value}</strong></span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* State-wise + Recent Companies */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Companies by State</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stateWiseData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis dataKey="state" type="category" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={80} />
                  <Tooltip />
                  <Bar dataKey="companies" fill="hsl(var(--accent))" radius={[0, 4, 4, 0]} barSize={14} name="Companies" />
                  <Bar dataKey="users" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} barSize={14} name="Users" />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Weekly Signups vs Churns</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dailySignups}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="signups" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="churns" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </TabsContent>

        {/* ── Revenue Tab ── */}
        <TabsContent value="revenue" className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Total MRR" value="₹4,52,000" sub="+₹27K from last month" icon={IndianRupee} positive />
            <MetricCard label="ARR" value="₹54,24,000" sub="+18% YoY" icon={TrendingUp} positive />
            <MetricCard label="ARPU" value="₹540" sub="+₹12 from last month" icon={Users} positive />
            <MetricCard label="LTV" value="₹16,200" sub="Avg. 30 months retention" icon={CreditCard} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">MRR vs ARR Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v: number) => [`₹${v.toLocaleString("en-IN")}`, ""]} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="mrr" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} name="MRR" />
                  <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} name="Total Revenue" />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Revenue by Plan</h3>
              <div className="space-y-4">
                {planDistribution.map((p, i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                        <span className="text-foreground font-medium">{p.name}</span>
                      </div>
                      <span className="text-muted-foreground">₹{p.revenue.toLocaleString("en-IN")}/mo</span>
                    </div>
                    <Progress value={p.pct * 2.5} className="h-2" />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{p.value} companies</span>
                      <span>{p.pct}% of base</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* ── Growth Tab ── */}
        <TabsContent value="growth" className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="New Companies (MTD)" value="8" sub="+33% vs last month" icon={Building2} positive />
            <MetricCard label="Churned" value="5" sub="4.0% churn rate" icon={UserMinus} positive={false} />
            <MetricCard label="Net Growth" value="+3" sub="Positive trend" icon={TrendingUp} positive />
            <MetricCard label="New Users (MTD)" value="45" sub="+104% vs last month" icon={UserPlus} positive />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Company Growth (New vs Churned)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="newCompanies" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} name="New" />
                  <Bar dataKey="churned" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} name="Churned" />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Net Growth Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={growthData}>
                  <defs>
                    <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="netGrowth" stroke="hsl(var(--accent))" fill="url(#netGrad)" strokeWidth={2} name="Net Companies" />
                  <Line type="monotone" dataKey="newUsers" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} name="New Users" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* User Growth by Month */}
          <Card>
            <h3 className="text-sm font-semibold text-foreground mb-4">User Acquisition vs Churn</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="newUsers" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 4 }} name="Acquired" />
                <Line type="monotone" dataKey="churnedUsers" stroke="hsl(var(--destructive))" strokeWidth={2} dot={{ r: 4 }} name="Churned" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        {/* ── Features Tab ── */}
        <TabsContent value="features" className="space-y-5">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Most Used Features</h3>
              <span className="text-xs text-muted-foreground">By % of active companies</span>
            </div>
            <div className="space-y-3">
              {featureUsage.map((f, i) => (
                <motion.div key={f.name} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                  <div className="flex items-center gap-3">
                    <div className="w-6 text-center text-xs font-bold text-muted-foreground">#{i + 1}</div>
                    <div className="p-1.5 rounded bg-accent/10"><f.icon className="h-3.5 w-3.5 text-accent" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">{f.name}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">{f.sessions.toLocaleString()} sessions</span>
                          <Badge variant={f.usage >= 80 ? "default" : f.usage >= 50 ? "secondary" : "outline"} className="text-[10px]">{f.usage}%</Badge>
                        </div>
                      </div>
                      <Progress value={f.usage} className="h-1.5" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Feature Adoption Rate</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={featureUsage.slice(0, 8)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={90} />
                  <Tooltip formatter={(v: number) => [`${v}%`, "Adoption"]} />
                  <Bar dataKey="usage" fill="hsl(var(--accent))" radius={[0, 4, 4, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Sessions by Feature</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={featureUsage.slice(0, 6)} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="sessions" nameKey="name">
                    {featureUsage.slice(0, 6).map((_, i) => (
                      <Cell key={i} fill={["hsl(var(--accent))", "hsl(var(--primary))", "hsl(220 14% 70%)", "hsl(var(--muted-foreground))", "hsl(220 14% 80%)", "hsl(220 14% 60%)"][i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => [v.toLocaleString(), "Sessions"]} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </TabsContent>

        {/* ── Subscriptions Tab ── */}
        <TabsContent value="subscriptions" className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Trial→Paid Conversion" value={`${subscriptionMetrics.trialToPayConversion}%`} sub="8 trials expiring soon" icon={Percent} positive />
            <MetricCard label="Renewal Rate" value={`${subscriptionMetrics.renewalRate}%`} sub="3 overdue renewals" icon={CheckCircle2} positive />
            <MetricCard label="Monthly Churn" value={`${subscriptionMetrics.monthlyChurnRate}%`} sub="Industry avg: 5%" icon={UserMinus} positive />
            <MetricCard label="Upgrade Rate" value={`${subscriptionMetrics.upgradeRate}%`} sub="Starter → Professional" icon={ArrowUpRight} positive />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Subscription Distribution</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={planDistribution} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name.split(" ")[0]} ${(percent * 100).toFixed(0)}%`}>
                    {planDistribution.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Subscription Funnel</h3>
              <div className="space-y-4 mt-6">
                {[
                  { stage: "Website Visitors", count: 3200, pct: 100 },
                  { stage: "Free Trial Signups", count: 26, pct: 0.8 },
                  { stage: "Active Trial Users", count: 18, pct: 69 },
                  { stage: "Paid Conversions", count: 9, pct: 34 },
                  { stage: "Retained (6mo+)", count: 72, pct: 92 },
                ].map((s, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{s.stage}</span>
                      <span className="text-muted-foreground font-mono">{s.count.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2.5">
                      <div className="h-2.5 rounded-full bg-accent transition-all" style={{ width: `${Math.max(s.pct, 3)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Recent Companies Table */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground">Recent Companies</h3>
              <span className="text-xs text-accent cursor-pointer hover:underline">View All →</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {["Company", "State", "Plan", "Users", "MRR", "Status"].map((h) => (
                      <th key={h} className="text-left py-2.5 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentCompanies.map((c, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-2.5 px-2 font-medium text-foreground">{c.name}</td>
                      <td className="py-2.5 px-2 text-muted-foreground">{c.state}</td>
                      <td className="py-2.5 px-2"><Badge variant="outline" className="text-xs">{c.plan}</Badge></td>
                      <td className="py-2.5 px-2 text-muted-foreground">{c.users}</td>
                      <td className="py-2.5 px-2 font-medium text-foreground">{c.revenue}</td>
                      <td className="py-2.5 px-2">
                        <Badge variant={c.status === "Active" ? "default" : "secondary"} className="text-xs gap-1">
                          {c.status === "Active" ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />} {c.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Marketplace Tab ── */}
        <TabsContent value="marketplace" className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Total Registered CAs" value="48" sub="+6 this month" icon={Users} positive />
            <MetricCard label="Total Service Requests" value="187" sub="+23 this month" icon={FileText} positive />
            <MetricCard label="Marketplace Revenue" value="₹3,84,000" sub="+₹48K from last month" icon={IndianRupee} positive />
            <MetricCard label="Avg. Match Score" value="87%" sub="AI-powered matching" icon={Zap} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">Marketplace Revenue Trend</h3>
                <span className="text-xs text-muted-foreground">Last 6 months</span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={marketplaceRevenueData}>
                  <defs>
                    <linearGradient id="mktRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v: number) => [`₹${v.toLocaleString("en-IN")}`, ""]} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" fill="url(#mktRevGrad)" strokeWidth={2} name="Revenue" />
                  <Area type="monotone" dataKey="commission" stroke="hsl(var(--primary))" fill="none" strokeWidth={2} strokeDasharray="5 5" name="Commission" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Most Popular Services</h3>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={popularServicesData} cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={3} dataKey="value">
                    {popularServicesData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: number, name: string) => [v, name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-1 gap-1.5 mt-2">
                {popularServicesData.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                      <span className="text-muted-foreground">{s.name}</span>
                    </div>
                    <span className="font-medium text-foreground">{s.value} requests</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Top Performing CAs */}
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Top Performing CAs</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {["CA Name", "City", "Rating", "Clients", "Revenue"].map((h) => (
                        <th key={h} className="text-left py-2.5 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {topPerformingCAs.map((ca, i) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-2.5 px-2">
                          <div>
                            <span className="font-medium text-foreground">{ca.name}</span>
                            {ca.verified && <Badge variant="outline" className="ml-2 text-[10px] text-accent border-accent/30">Verified</Badge>}
                          </div>
                          <span className="text-xs text-muted-foreground">{ca.firm}</span>
                        </td>
                        <td className="py-2.5 px-2 text-muted-foreground">{ca.city}</td>
                        <td className="py-2.5 px-2">
                          <span className="text-foreground font-medium">⭐ {ca.rating}</span>
                          <span className="text-muted-foreground text-xs ml-1">({ca.reviews})</span>
                        </td>
                        <td className="py-2.5 px-2 text-muted-foreground">{ca.clients}</td>
                        <td className="py-2.5 px-2 font-medium text-foreground">{ca.revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Service Request Status Breakdown */}
            <Card>
              <h3 className="text-sm font-semibold text-foreground mb-4">Request Status Breakdown</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={requestStatusData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="status" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={32}>
                    {requestStatusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-lg font-bold text-foreground">72%</p>
                  <p className="text-xs text-muted-foreground">Completion Rate</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-lg font-bold text-foreground">3.2 days</p>
                  <p className="text-xs text-muted-foreground">Avg. Response Time</p>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* ── System Tab ── */}
        <TabsContent value="system" className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Server className="h-4 w-4 text-accent" />
                <h3 className="text-sm font-semibold text-foreground">System Health</h3>
              </div>
              <div className="space-y-3">
                {systemHealth.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <span className="text-sm text-muted-foreground">{item.metric}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{item.value}</span>
                      <div className={`w-2.5 h-2.5 rounded-full ${item.status === "good" ? "bg-emerald-500" : "bg-amber-500"}`} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Activity className="h-4 w-4 text-accent" />
                <h3 className="text-sm font-semibold text-foreground">Platform Metrics</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Avg Session", value: "12m 34s" },
                  { label: "Daily Active", value: "342" },
                  { label: "API Calls (24h)", value: "28,450" },
                  { label: "Vouchers/Day", value: "1,240" },
                  { label: "Invoices/Day", value: "890" },
                  { label: "GST Filings (MTD)", value: "67" },
                ].map((m, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/50">
                    <p className="text-lg font-bold text-foreground">{m.value}</p>
                    <p className="text-xs text-muted-foreground">{m.label}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Danger Zone */}
      <DangerZone />
    </div>
  );
};

export default SuperAdminDashboard;
